import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { ArrowPathRoundedSquareIcon, HeartIcon, ChatBubbleLeftIcon } from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';
import { doc, getDoc, setDoc, updateDoc, deleteDoc, increment, serverTimestamp, collection } from 'firebase/firestore';
import { db } from '../../firebase/config';
import AuthContext from '../../contexts/AuthContext';
import PostCard from './PostCard';
import FormattedText from '../common/FormattedText';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import { es, enUS } from 'date-fns/locale';

interface RepostProps {
  post: {
    id: string;
    userId: string;
    userName: string;
    userPhotoURL: string;
    content: string;
    createdAt: any;
    likes?: number;
    comments?: number;
    type?: 'post' | 'repost';
    originalPostId?: string;
  };
  children?: React.ReactNode;
  originalPost?: any;
}

const RepostCard: React.FC<RepostProps> = ({ post, children, originalPost }) => {
  const { t, i18n } = useTranslation();
  const { currentUser } = useContext(AuthContext);
  const [liked, setLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(post.likes || 0);

  const formatPostDate = (timestamp: any) => {
    if (!timestamp) return '';

    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const locale = i18n.language.startsWith('es') ? es : enUS;
    return formatDistanceToNow(date, { addSuffix: true, locale });
  };

  // Check if current user has liked this repost
  useEffect(() => {
    const checkLike = async () => {
      if (!currentUser) return;

      try {
        const likeDocRef = doc(db, `posts/${post.id}/likes`, currentUser.uid);
        const likeDoc = await getDoc(likeDocRef);
        setLiked(likeDoc.exists());
      } catch (error) {
        console.error('Error checking like:', error);
      }
    };

    checkLike();
  }, [currentUser, post.id]);

  const handleLike = async () => {
    if (!currentUser) return;

    try {
      const postRef = doc(db, 'posts', post.id);
      const likeDocRef = doc(db, `posts/${post.id}/likes`, currentUser.uid);

      if (!liked) {
        // Add like
        await Promise.all([
          updateDoc(postRef, {
            likes: increment(1)
          }),
          setDoc(likeDocRef, {
            userId: currentUser.uid,
            userName: currentUser.displayName,
            createdAt: serverTimestamp()
          })
        ]);

        // Notify repost author if it's not the current user
        if (post.userId !== currentUser.uid) {
          const notificationRef = doc(collection(db, 'notifications'));
          await setDoc(notificationRef, {
            type: 'like',
            fromUserId: currentUser.uid,
            fromUserName: currentUser.displayName,
            fromUserPhoto: currentUser.photoURL || '',
            toUserId: post.userId,
            postId: post.id,
            read: false,
            createdAt: serverTimestamp()
          });
        }

        setLiked(true);
        setLikesCount(prev => prev + 1);
      } else {
        // Remove like
        await Promise.all([
          updateDoc(postRef, {
            likes: increment(-1)
          }),
          deleteDoc(likeDocRef)
        ]);

        setLiked(false);
        setLikesCount(prev => prev - 1);
      }
    } catch (error) {
      console.error('Error updating like:', error);
    }
  };

  return (
    <article className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
      {/* Repost header */}
      <div className="flex items-start mb-4">
        <Link
          to={`/profile/${post.userId}`}
          className="flex-shrink-0"
          aria-label={t('post.viewProfile', { user: post.userName })}
        >
          <img
            src={post.userPhotoURL || '/default-avatar.png'}
            alt=""
            className="h-10 w-10 rounded-full object-cover"
            aria-hidden="true"
          />
        </Link>
        <div className="ml-3 flex-grow">
          <div className="flex items-center">
            <Link
              to={`/profile/${post.userId}`}
              className="font-medium hover:underline"
            >
              {post.userName}
            </Link>
            <div className="flex items-center ml-2 text-gray-500 dark:text-gray-400">
              <ArrowPathRoundedSquareIcon className="h-4 w-4 mr-1" />
              <span className="text-sm">
                {t('post.reposted')}
              </span>
            </div>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {formatPostDate(post.createdAt)}
          </p>
          {post.content && (
            <div className="text-gray-800 dark:text-gray-200 mt-2 mb-3 whitespace-pre-line break-words">
              <FormattedText text={post.content} />
            </div>
          )}
        </div>
      </div>

      {/* Original post */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-gray-50 dark:bg-gray-750">
        {children ? (
          children
        ) : originalPost ? (
          <PostCard post={originalPost} isReposted={true} />
        ) : null}
      </div>

      {/* Repost actions */}
      <div className="flex items-center gap-4 mt-4 border-t border-gray-100 dark:border-gray-700 pt-3">
        <button
          onClick={handleLike}
          aria-label={liked ? t('post.unlike') : t('post.like')}
          aria-pressed={liked}
          className={`flex items-center text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-all duration-300 ease-in-out transform hover:scale-110 active:scale-90 ${liked ? 'text-red-500 dark:text-red-400' : ''
            }`}
        >
          {liked ? (
            <HeartSolidIcon className="h-5 w-5 mr-1 text-red-500 dark:text-red-400 animate-heart-beat" aria-hidden="true" />
          ) : (
            <HeartIcon className="h-5 w-5 mr-1 transition-transform duration-300 ease-in-out hover:scale-110" aria-hidden="true" />
          )}
          <span aria-live="polite" className={`font-medium ${liked ? 'text-red-500 dark:text-red-400' : ''}`}>
            {likesCount}
          </span>
        </button>

        <button
          className="flex items-center text-gray-500 dark:text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 transition-all duration-300 ease-in-out transform hover:scale-110 active:scale-90"
          aria-label={t('post.comment')}
        >
          <ChatBubbleLeftIcon className="h-5 w-5 mr-1" aria-hidden="true" />
          <span className="font-medium">{post.comments || 0}</span>
        </button>
      </div>
    </article>
  );
};

export default RepostCard;
